/**
 * Utility for handling persistent query parameters across routes and domain redirects
 * Manages localStorage and URL query parameter synchronization
 */
import { Logger } from '../mixins/logger';

/**
 * Configuration for parameters that should be persisted
 * @typedef {Object} ParamConfig
 * @property {string} storageKey - Key to use in localStorage
 * @property {boolean} persistAcrossDomains - Whether to persist across domain redirects
 * @property {boolean} clearOnSuccess - Whether to clear from storage after successful use
 * @property {number} [ttlMs] - Optional time-to-live; stored value expires this many ms after capture
 */

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

/** Marketing attribution tags captured on landing and attached to analytics events and API calls */
export const ATTRIBUTION_PARAMS = ['campaign', 'partner'];

/**
 * Default configuration for known parameters
 * @type {Object.<string, ParamConfig>}
 */
const DEFAULT_PARAM_CONFIG = {
  campaign: {
    storageKey: 'pendingCampaign',
    persistAcrossDomains: true,
    clearOnSuccess: false,
    ttlMs: THIRTY_DAYS_MS
  },
  partner: {
    storageKey: 'pendingPartner',
    persistAcrossDomains: true,
    clearOnSuccess: false,
    ttlMs: THIRTY_DAYS_MS
  },
  viewAs: {
    storageKey: 'pendingViewAs',
    persistAcrossDomains: true,
    clearOnSuccess: true
  },
  utm_source: {
    storageKey: 'pendingUtmSource',
    persistAcrossDomains: true,
    clearOnSuccess: false
  },
  utm_campaign: {
    storageKey: 'pendingUtmCampaign',
    persistAcrossDomains: true,
    clearOnSuccess: false
  },
  utm_content: {
    storageKey: 'pendingUtmContent',
    persistAcrossDomains: true,
    clearOnSuccess: false
  },
  utm_medium: {
    storageKey: 'pendingUtmMedium',
    persistAcrossDomains: true,
    clearOnSuccess: false
  },
  session_id: {
    storageKey: 'pendingSessionId',
    persistAcrossDomains: true,
    clearOnSuccess: false
  },
  referrer_url: {
    storageKey: 'pendingReferrerUrl',
    persistAcrossDomains: true,
    clearOnSuccess: false
  },
  gclid: {
    storageKey: 'pendingGclid',
    persistAcrossDomains: true,
    clearOnSuccess: false
  },
  fbclid: {
    storageKey: 'pendingFbclid',
    persistAcrossDomains: true,
    clearOnSuccess: false
  },
  landing_page: {
    storageKey: 'pendingLandingPage',
    persistAcrossDomains: true,
    clearOnSuccess: false
  },
  submission_timestamp: {
    storageKey: 'pendingSubmissionTimestamp',
    persistAcrossDomains: true,
    clearOnSuccess: false
  },
  course_offering_id: {
    storageKey: 'pendingCourseOfferingId',
    persistAcrossDomains: true,
    clearOnSuccess: true
  },
  pathway: {
    storageKey: 'pendingPathway',
    persistAcrossDomains: true,
    clearOnSuccess: true
  }
};

/**
 * Get current URL search parameters
 * @param {string} [url] - URL to parse (defaults to current window location)
 * @returns {URLSearchParams} - URLSearchParams object
 */
export function getCurrentUrlParams(url = null) {
  const targetUrl = url || (typeof window !== 'undefined' ? window.location.search : '');
  return new URLSearchParams(targetUrl);
}

/**
 * Store a parameter value in localStorage with a pending prefix
 * @param {string} paramName - Name of the parameter
 * @param {string} value - Value to store
 * @param {ParamConfig} [config] - Configuration for the parameter
 */
export function storePendingParam(paramName, value, config = null) {
  if (!value) return;

  const paramConfig = config ||
    DEFAULT_PARAM_CONFIG[paramName] || {
      storageKey: `pending${paramName.charAt(0).toUpperCase()}${paramName.slice(1)}`,
      persistAcrossDomains: true,
      clearOnSuccess: true
    };

  try {
    localStorage.setItem(paramConfig.storageKey, value);
    // Last-touch: every capture refreshes the expiry window
    if (paramConfig.ttlMs) {
      localStorage.setItem(`${paramConfig.storageKey}ExpiresAt`, String(Date.now() + paramConfig.ttlMs));
    }
  } catch (error) {
    Logger.methods.logError(`Failed to store pending parameter ${paramName}`, error);
  }
}

/**
 * Retrieve a parameter value from localStorage
 * @param {string} paramName - Name of the parameter
 * @param {ParamConfig} [config] - Configuration for the parameter
 * @returns {string|null} - Retrieved value or null
 */
export function getPendingParam(paramName, config = null) {
  const paramConfig = config ||
    DEFAULT_PARAM_CONFIG[paramName] || {
      storageKey: `pending${paramName.charAt(0).toUpperCase()}${paramName.slice(1)}`,
      persistAcrossDomains: true,
      clearOnSuccess: true
    };

  try {
    if (paramConfig.ttlMs) {
      // A missing/invalid expiry counts as expired so a tag can never become permanent
      const expiresAt = Number(localStorage.getItem(`${paramConfig.storageKey}ExpiresAt`));
      if (!expiresAt || Date.now() > expiresAt) {
        localStorage.removeItem(paramConfig.storageKey);
        localStorage.removeItem(`${paramConfig.storageKey}ExpiresAt`);
        return null;
      }
    }
    return localStorage.getItem(paramConfig.storageKey);
  } catch (error) {
    Logger.methods.logError(`Failed to retrieve pending parameter ${paramName}`, error);
    return null;
  }
}

/**
 * Clear a parameter from localStorage
 * @param {string} paramName - Name of the parameter
 * @param {ParamConfig} [config] - Configuration for the parameter
 */
export function clearPendingParam(paramName, config = null) {
  const paramConfig = config ||
    DEFAULT_PARAM_CONFIG[paramName] || {
      storageKey: `pending${paramName.charAt(0).toUpperCase()}${paramName.slice(1)}`,
      persistAcrossDomains: true,
      clearOnSuccess: true
    };

  try {
    localStorage.removeItem(paramConfig.storageKey);
    localStorage.removeItem(`${paramConfig.storageKey}ExpiresAt`);
  } catch (error) {
    Logger.methods.logError(`Failed to clear pending parameter ${paramName}`, error);
  }
}

/**
 * Get a parameter value from either URL or localStorage
 * @param {string} paramName - Name of the parameter
 * @param {URLSearchParams} [urlParams] - URL parameters (defaults to current URL)
 * @param {ParamConfig} [config] - Configuration for the parameter
 * @returns {string|null} - Parameter value from URL or storage
 */
export function getParamValue(paramName, urlParams = null, config = null) {
  const currentUrlParams = urlParams || getCurrentUrlParams();
  const urlValue = currentUrlParams.get(paramName);
  const storageValue = getPendingParam(paramName, config);

  // Prefer URL value over storage value
  return urlValue || storageValue;
}

/**
 * Store parameters from URL into localStorage for later use
 * @param {Array<string>} paramNames - Array of parameter names to store
 * @param {URLSearchParams} [urlParams] - URL parameters (defaults to current URL)
 * @param {Object.<string, ParamConfig>} [customConfig] - Custom configuration for parameters
 */
export function storeParamsFromUrl(paramNames, urlParams = null, customConfig = {}) {
  const currentUrlParams = urlParams || getCurrentUrlParams();

  paramNames.forEach((paramName) => {
    const value = currentUrlParams.get(paramName);
    if (value) {
      const config = customConfig[paramName] || DEFAULT_PARAM_CONFIG[paramName];
      storePendingParam(paramName, value, config);
    }
  });
}

/**
 * Add persistent parameters to a URLSearchParams object
 * @param {URLSearchParams} queryParams - URLSearchParams object to modify
 * @param {Array<string>} paramNames - Array of parameter names to include
 * @param {URLSearchParams} [urlParams] - Current URL parameters (defaults to current URL)
 * @param {Object.<string, ParamConfig>} [customConfig] - Custom configuration for parameters
 * @returns {URLSearchParams} - Modified URLSearchParams object
 */
export function addPersistentParams(queryParams, paramNames, urlParams = null, customConfig = {}) {
  paramNames.forEach((paramName) => {
    const config = customConfig[paramName] || DEFAULT_PARAM_CONFIG[paramName];
    const value = getParamValue(paramName, urlParams, config);

    if (value) {
      queryParams.set(paramName, value);
    }
  });

  return queryParams;
}

/**
 * Clear parameters from localStorage after successful processing
 * @param {Array<string>} paramNames - Array of parameter names to potentially clear
 * @param {Object.<string, ParamConfig>} [customConfig] - Custom configuration for parameters
 */
export function clearProcessedParams(paramNames, customConfig = {}) {
  paramNames.forEach((paramName) => {
    const config = customConfig[paramName] || DEFAULT_PARAM_CONFIG[paramName];

    if (config && config.clearOnSuccess) clearPendingParam(paramName, config);
  });
}

/**
 * Handle parameter persistence for domain redirects
 * @param {Array<string>} paramNames - Array of parameter names to handle
 * @param {URLSearchParams} [additionalParams] - Additional URL parameters to check (e.g., from requestedURL)
 * @param {Object.<string, ParamConfig>} [customConfig] - Custom configuration for parameters
 * @returns {Object} - Object with parameters to include in redirect and storage operations performed
 */
export function handleRedirectParams(paramNames, additionalParams = null, customConfig = {}) {
  const currentUrlParams = getCurrentUrlParams();
  const redirectParams = {};
  const storedParams = [];

  paramNames.forEach((paramName) => {
    const config = customConfig[paramName] || DEFAULT_PARAM_CONFIG[paramName];

    // Check current URL first, then additional params (like requestedURL), then storage
    const currentUrlValue = currentUrlParams.get(paramName);
    const additionalValue = additionalParams?.get(paramName);
    const storageValue = getPendingParam(paramName, config);

    // Priority: current URL > additional params > storage
    const valueToUse = currentUrlValue || additionalValue || storageValue;

    if (valueToUse) {
      // Always include in redirect if we have a value
      redirectParams[paramName] = valueToUse;

      // Store in localStorage if it came from URL sources and we don't already have it stored
      const urlSourceValue = currentUrlValue || additionalValue;
      if (urlSourceValue && !storageValue && config.persistAcrossDomains) {
        storePendingParam(paramName, urlSourceValue, config);
        storedParams.push(paramName);
      }
    }
  });

  return {
    redirectParams,
    storedParams
  };
}

/**
 * Get the marketing attribution tags (campaign, partner) from URL or storage.
 * Values are lowercased so every consumer emits the identical value.
 * @param {URLSearchParams} [urlParams] - URL parameters (defaults to current URL)
 * @returns {{campaign: string, partner: string}} - Attribution values, '' when absent
 */
export function getAttributionParams(urlParams = null) {
  const result = {};
  ATTRIBUTION_PARAMS.forEach((paramName) => {
    result[paramName] = (getParamValue(paramName, urlParams) || '').toLowerCase();
  });
  return result;
}

/**
 * Get the attribution tags as request headers for API calls.
 * Values are sanitized to valid header characters — control characters would make
 * fetch/axios throw before the request is sent.
 * @returns {Object} - x-campaign/x-partner headers, keys omitted when a tag is absent
 */
export function getAttributionHeaders() {
  const { campaign, partner } = getAttributionParams();
  const sanitize = (value) => value.replace(/[^\x20-\x7E]/g, '').slice(0, 100);
  const safeCampaign = sanitize(campaign);
  const safePartner = sanitize(partner);

  return {
    ...(safeCampaign && { 'x-campaign': safeCampaign }),
    ...(safePartner && { 'x-partner': safePartner })
  };
}

/**
 * Get all configured persistent parameters
 * @returns {Array<string>} - Array of all configured parameter names
 */
export function getAllConfiguredParams() {
  return Object.keys(DEFAULT_PARAM_CONFIG);
}

/**
 * Validate parameter configuration
 * @param {ParamConfig} config - Configuration to validate
 * @returns {boolean} - Whether the configuration is valid
 */
export function isValidParamConfig(config) {
  return (
    config &&
    typeof config.storageKey === 'string' &&
    typeof config.persistAcrossDomains === 'boolean' &&
    typeof config.clearOnSuccess === 'boolean'
  );
}
